import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Avatar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  TextField
} from '@mui/material';
import sectionAvatarService from '../../../services/sectionAvatarService';
import sectionColorService from '../../../services/sectionColorService';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import CustomAvatarSelector from './CustomAvatarSelector';

/**
 * Section Orchestrator Component for Foundation V1 Page
 * Shows an orchestrator avatar and name for each section
 */
const SectionOrchestrator = ({
  sectionId,
  sectionName,
  projectId,
  avatarUrl,
  avatarName,
  onOrchestratorUpdate
}) => {
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [imageError, setImageError] = useState(false);

  const handleOpenDialog = () => {
    setSelectedAvatar(null);
    setName(avatarName || '');
    setError('');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleSelectAvatar = (avatar) => {
    if (!avatar) return;
    setSelectedAvatar(avatar);
    if (avatar.name) {
      setName(avatar.name);
    }
  };

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Please enter a name');
      return;
    }

    const photoUrl = selectedAvatar?.photoUrl || avatarUrl;
    if (!photoUrl) {
      setError('Please select an avatar');
      return;
    }

    try {
      setSaving(true);
      setError('');

      await sectionAvatarService.saveSectionAvatar(sectionId, projectId, {
        sectionId,
        avatarName: trimmedName,
        photoUrl,
        type: selectedAvatar?.type || 'robot',
        projectId
      });

      // Update cache - this triggers re-render with new props
      onOrchestratorUpdate?.(sectionId, {
        name: trimmedName,
        avatarUrl: photoUrl
      });

      setDialogOpen(false);
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const displayUrl = imageError ? null : avatarUrl;
  const displayName = avatarName || '';

  return (
    <>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        mt: 1,
        mb: 2,
        py: 0.5
      }}>
        {displayName && (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontSize: '0.8rem',
              fontWeight: 500,
              mb: 1
            }}
          >
            Orchestrator:
          </Typography>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2.5,
            mt: 0.5,
            ml: 0.5
          }}
        >
          <Box
            sx={{
              position: 'relative',
              cursor: projectId ? 'pointer' : 'default',
              '&:hover': projectId ? {
                opacity: 0.9,
                transform: 'scale(1.05)',
                transition: 'all 0.2s'
              } : {}
            }}
            onClick={projectId ? handleOpenDialog : undefined}
          >
            {displayUrl ? (
              <Avatar
                src={displayUrl}
                alt={displayName}
                onError={() => setImageError(true)}
                sx={{
                  width: 48,
                  height: 48,
                  backgroundColor: sectionColorService.getSectionBackgroundColor(sectionId),
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              />
            ) : (
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  fontSize: '1.2rem',
                  background: sectionColorService.getSectionBackgroundColor(sectionId),
                  color: sectionColorService.getSectionIconColor(sectionId),
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              >
                {displayName ? displayName.charAt(0).toUpperCase() : ''}
              </Avatar>
            )}

            {projectId && (
              <Box
                sx={{
                  position: 'absolute',
                  right: -6,
                  bottom: -6,
                  bgcolor: 'primary.main',
                  color: 'white',
                  borderRadius: '50%',
                  width: 22,
                  height: 22,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  border: '1px solid white'
                }}
              >
                <EditIcon sx={{ fontSize: '0.8rem' }} />
              </Box>
            )}
          </Box>

          {displayName && (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: '0.9rem',
                color: '#333',
                mt: 0.75
              }}
            >
              {displayName}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Avatar Selection Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '4px',
            maxWidth: '90vw',
            width: '90vw',
            maxHeight: '90vh',
            height: '90vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 3,
          pl: 6,
          pr: 5,
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
        }}>
          <Typography variant="h6" sx={{ fontFamily: "'Sofia Pro Bold', sans-serif", fontWeight: 700 }}>
            {sectionName} Orchestrator
          </Typography>
          <IconButton onClick={handleCloseDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 4, display: 'flex', flexDirection: 'column' }}>
          {/* Preview and Name Input */}
          <Box sx={{
            mt: 3,
            mb: 3,
            position: 'sticky',
            top: 0,
            zIndex: 10,
            backgroundColor: 'white',
            pt: 2,
            pb: 5.5,
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              {/* Avatar Preview */}
              <Box>
                {(selectedAvatar?.photoUrl || avatarUrl) ? (
                  <Box
                    component="img"
                    src={selectedAvatar?.photoUrl || avatarUrl}
                    alt="Selected avatar"
                    sx={{
                      width: 65,
                      height: 65,
                      borderRadius: '50%',
                      border: '3px solid #FFF5C0',
                      objectFit: 'contain',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                    }}
                  />
                ) : (
                  <Avatar
                    sx={{
                      width: 65,
                      height: 65,
                      fontSize: 24,
                      bgcolor: sectionColorService.getSectionBackgroundColor(sectionId),
                      color: sectionColorService.getSectionIconColor(sectionId),
                      border: '3px solid #FFF5C0'
                    }}
                  >
                    {name ? name.charAt(0).toUpperCase() : ''}
                  </Avatar>
                )}
              </Box>

              {/* Name Input */}
              <Box sx={{ flexGrow: 1, ml: 3 }}>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                  Orchestrator Name
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (error) setError('');
                  }}
                  placeholder="Enter orchestrator name"
                  autoFocus
                  error={!!error}
                  helperText={error || "Enter a name for this orchestrator"}
                  InputProps={{
                    sx: { borderRadius: '4px', height: '45px' }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#485869'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#485869'
                      }
                    }
                  }}
                />
              </Box>
            </Box>
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontSize: '16px' }}>
            Select an orchestrator
          </Typography>

          <CustomAvatarSelector
            maxHeight='calc(100vh - 440px)'
            onSelectAvatar={handleSelectAvatar}
          />
        </DialogContent>

        <DialogActions sx={{ borderTop: '1px solid', borderColor: 'divider', px: 3, py: 2 }}>
          <Button onClick={handleCloseDialog} color="inherit" variant='outlined'>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={!name.trim() || saving}
            startIcon={saving && <CircularProgress size={16} color="inherit" />}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

SectionOrchestrator.propTypes = {
  sectionId: PropTypes.string,
  sectionName: PropTypes.string.isRequired,
  projectId: PropTypes.string,
  avatarUrl: PropTypes.string,
  avatarName: PropTypes.string,
  onOrchestratorUpdate: PropTypes.func
};

export default SectionOrchestrator;
